import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { PERSONAL_SCOPE, itemAad } from "@passwordflow/crypto";
import { notifyUnauthorized } from "../app/authEvents.js";
import { useSession } from "../app/session.js";
import type { SessionState } from "../app/session.js";
import { createItem, deleteItem, listItems, openItem, patchItem, restoreItem } from "../api/vault.js";
import type { VaultItemFull, VaultMeta } from "../api/vault.js";
import { ApiClientError } from "../api/http.js";
import { b64ToUtf8, bytesToB64, utf8ToB64 } from "../b64.js";
import type { CryptoClient } from "../cryptoClient.js";
import type { Entry } from "../lib/entries.js";
import { LEGACY_VAULT_AAD, parsePayload, serializePayload } from "../vaultPayload.js";
import type { VaultPayload } from "../vaultPayload.js";

export const ENTRIES_KEY: ReadonlyArray<string> = ["vault", "entries"];
export const TRASH_KEY: ReadonlyArray<string> = ["vault", "trash"];

const DECRYPT_CONCURRENCY = 6;

export interface VaultLoad {
  readonly entries: ReadonlyArray<Entry>;
  readonly skipped: number;
}

export interface SaveInput {
  readonly id?: string | undefined;
  readonly version?: number | undefined;
  readonly folder: string;
  readonly favorite: boolean;
  readonly payload: VaultPayload;
}

function toMeta(full: VaultItemFull): VaultMeta {
  return {
    id: full.id,
    folder: full.folder,
    titleHash: full.titleHash,
    version: full.version,
    favorite: full.favorite,
    updatedAt: full.updatedAt,
    deletedAt: full.deletedAt
  };
}

function clientOf(state: SessionState): CryptoClient {
  if (state.status === "locked") {
    throw new Error("Der Tresor ist gesperrt.");
  }
  return state.client;
}

function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiClientError && err.status === 401;
}

interface PersonalPlain {
  readonly plain: string;
  /** True when the item only opened with the old id-independent AAD and must be re-encrypted. */
  readonly legacy: boolean;
}

/**
 * Opens a personal item with its id-bound AAD. The id-independent AAD (v1) is accepted only here so
 * loadVault can re-encrypt such items on unlock; nothing else writes or reads it any more.
 */
async function decryptPersonal(client: CryptoClient, full: VaultItemFull): Promise<PersonalPlain> {
  try {
    return { plain: await client.decrypt(full.nonceB64, full.ciphertextB64, bytesToB64(itemAad(PERSONAL_SCOPE, full.id))), legacy: false };
  } catch {
    return { plain: await client.decrypt(full.nonceB64, full.ciphertextB64, utf8ToB64(LEGACY_VAULT_AAD)), legacy: true };
  }
}

/** Best effort: a failed or conflicting upgrade leaves the item readable and is retried on the next unlock. */
async function upgradeLegacyItem(client: CryptoClient, full: VaultItemFull, plain: string): Promise<VaultItemFull> {
  try {
    const sealed = await client.encrypt(plain, bytesToB64(itemAad(PERSONAL_SCOPE, full.id)));
    return await patchItem(full.id, {
      ciphertextB64: sealed.ciphertextB64,
      nonceB64: sealed.nonceB64,
      expectedVersion: full.version
    });
  } catch {
    return full;
  }
}

async function decryptEntry(client: CryptoClient, id: string): Promise<Entry | undefined> {
  let full = await openItem(id);
  const opened = await decryptPersonal(client, full);
  const data = parsePayload(b64ToUtf8(opened.plain));
  if (data !== undefined && opened.legacy) {
    full = await upgradeLegacyItem(client, full, opened.plain);
  }
  return data === undefined ? undefined : { meta: toMeta(full), data };
}

/** Runs task over items with a fixed number of in-flight requests. Failures are returned, not thrown. */
async function settleAll<T, R>(
  items: ReadonlyArray<T>,
  limit: number,
  task: (item: T) => Promise<R>
): Promise<Array<R | Error>> {
  const results: Array<R | Error> = [];
  let cursor = 0;
  async function worker(): Promise<void> {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      const item = items[index];
      if (item === undefined) {
        continue;
      }
      try {
        results[index] = await task(item);
      } catch (err: unknown) {
        results[index] = err instanceof Error ? err : new Error("Unbekannter Fehler.");
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, (): Promise<void> => worker()));
  return results;
}

export async function loadVault(client: CryptoClient): Promise<VaultLoad> {
  const metas = await listItems(false);
  const results = await settleAll(metas, DECRYPT_CONCURRENCY, (meta: VaultMeta): Promise<Entry | undefined> =>
    decryptEntry(client, meta.id)
  );
  const entries: Entry[] = [];
  let skipped = 0;
  for (const result of results) {
    if (result === undefined || result instanceof Error) {
      skipped += 1;
    } else {
      entries.push(result);
    }
  }
  return { entries, skipped };
}

function upsert(prev: VaultLoad | undefined, entry: Entry): VaultLoad | undefined {
  if (prev === undefined) {
    return undefined;
  }
  const exists = prev.entries.some((e: Entry): boolean => e.meta.id === entry.meta.id);
  const entries = exists
    ? prev.entries.map((e: Entry): Entry => (e.meta.id === entry.meta.id ? entry : e))
    : [entry, ...prev.entries];
  return { ...prev, entries };
}

export function useVaultEntries(): UseQueryResult<VaultLoad, Error> {
  const { state } = useSession();
  const client = state.status === "unlocked" ? state.client : undefined;
  return useQuery<VaultLoad, Error>({
    queryKey: ENTRIES_KEY,
    queryFn: async (): Promise<VaultLoad> => {
      if (client === undefined) {
        throw new Error("Der Tresor ist gesperrt.");
      }
      return loadVault(client);
    },
    enabled: client !== undefined
  });
}

export function useTrashItems(): UseQueryResult<VaultMeta[], Error> {
  const { state } = useSession();
  return useQuery<VaultMeta[], Error>({
    queryKey: TRASH_KEY,
    queryFn: async (): Promise<VaultMeta[]> => {
      const metas = await listItems(true);
      return metas.filter((meta: VaultMeta): boolean => meta.deletedAt !== null);
    },
    enabled: state.status === "unlocked"
  });
}

export function useSaveEntry(): UseMutationResult<Entry, Error, SaveInput> {
  const { state } = useSession();
  const queryClient = useQueryClient();
  return useMutation<Entry, Error, SaveInput>({
    mutationFn: async (input: SaveInput): Promise<Entry> => {
      const client = clientOf(state);
      const hash = await client.titleHash(PERSONAL_SCOPE, input.payload.title);
      const folder = input.folder.trim() === "" ? "Allgemein" : input.folder.trim();
      if (input.id === undefined) {
        const id = crypto.randomUUID();
        const sealed = await client.encrypt(
          utf8ToB64(serializePayload(input.payload)),
          bytesToB64(itemAad(PERSONAL_SCOPE, id))
        );
        const meta = await createItem({
          id,
          folder,
          titleHash: hash,
          ciphertextB64: sealed.ciphertextB64,
          nonceB64: sealed.nonceB64,
          favorite: input.favorite
        });
        return { meta, data: input.payload };
      }
      if (input.version === undefined) {
        throw new Error("Versionsnummer fehlt.");
      }
      const sealed = await client.encrypt(
        utf8ToB64(serializePayload(input.payload)),
        bytesToB64(itemAad(PERSONAL_SCOPE, input.id))
      );
      const full = await patchItem(input.id, {
        folder,
        titleHash: hash,
        ciphertextB64: sealed.ciphertextB64,
        nonceB64: sealed.nonceB64,
        favorite: input.favorite,
        expectedVersion: input.version
      });
      return { meta: toMeta(full), data: input.payload };
    },
    onSuccess: (entry: Entry): void => {
      queryClient.setQueryData<VaultLoad | undefined>(ENTRIES_KEY, (prev: VaultLoad | undefined) => upsert(prev, entry));
      void queryClient.invalidateQueries({ queryKey: TRASH_KEY });
    },
    onError: (err: Error): void => {
      if (isUnauthorized(err)) {
        notifyUnauthorized();
      }
    }
  });
}

export function useToggleFavorite(): UseMutationResult<Entry, Error, Entry> {
  const queryClient = useQueryClient();
  return useMutation<Entry, Error, Entry>({
    mutationFn: async (entry: Entry): Promise<Entry> => {
      const full = await patchItem(entry.meta.id, {
        favorite: !entry.meta.favorite,
        expectedVersion: entry.meta.version
      });
      return { meta: toMeta(full), data: entry.data };
    },
    onSuccess: (entry: Entry): void => {
      queryClient.setQueryData<VaultLoad | undefined>(ENTRIES_KEY, (prev: VaultLoad | undefined) => upsert(prev, entry));
    },
    onError: (err: Error): void => {
      if (isUnauthorized(err)) {
        notifyUnauthorized();
      }
    }
  });
}

export function useDeleteItem(): UseMutationResult<VaultMeta, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<VaultMeta, Error, string>({
    mutationFn: (id: string): Promise<VaultMeta> => deleteItem(id),
    onSuccess: (meta: VaultMeta): void => {
      queryClient.setQueryData<VaultLoad | undefined>(ENTRIES_KEY, (prev: VaultLoad | undefined) =>
        prev === undefined ? undefined : { ...prev, entries: prev.entries.filter((e: Entry): boolean => e.meta.id !== meta.id) }
      );
      void queryClient.invalidateQueries({ queryKey: TRASH_KEY });
    },
    onError: (err: Error): void => {
      if (isUnauthorized(err)) {
        notifyUnauthorized();
      }
    }
  });
}

export function useRestoreItem(): UseMutationResult<VaultMeta, Error, string> {
  const queryClient = useQueryClient();
  return useMutation<VaultMeta, Error, string>({
    mutationFn: (id: string): Promise<VaultMeta> => restoreItem(id),
    onSuccess: (): void => {
      void queryClient.invalidateQueries({ queryKey: ENTRIES_KEY });
      void queryClient.invalidateQueries({ queryKey: TRASH_KEY });
    },
    onError: (err: Error): void => {
      if (isUnauthorized(err)) {
        notifyUnauthorized();
      }
    }
  });
}
